import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage, { siteMessageWithCustomParserOptions } from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setFulfillmentMethodForCart from "@insite/client-framework/Store/Data/Carts/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouseForCart from "@insite/client-framework/Store/Data/Carts/Handlers/UpdatePickUpWarehouse";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Zone from "@insite/client-framework/Components/Zone";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import get from "@insite/mobius/utilities/get";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsUkWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import { COLORS, TYPOGRAPHY, WIDGETS } from "@tSurya/theme/design";
import React, { ChangeEvent, FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { DOMNode, domToReact, HTMLReactParserOptions } from "html-react-parser";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@tSurya/Overrides/Components/CartTotalDisplay";
import { DEFAULT_CHECKOUT_STEPS } from "@tSurya/Components/SurCheckoutStepper";
import { useSurCheckoutPayment } from "@tSurya/Components/SurCheckoutPaymentContext";
import { CHECKOUT_WIZARD_STEP, SurCheckoutWizardContext } from "@tSurya/Components/SurCheckoutWizardContext";
import {
    getPaymentBreakdown,
    getShippingPromotionTotal,
    getSplitTotals,
    hasMgbwTermsProperty,
    isTermsPaymentMethod,
    shouldShowSeparateMgbwChargeNotice,
} from "@tSurya/Common/Utilities/paymentBreakdown";
import CheckoutArrowLeftIcon from "@tSurya/Icons/CheckoutArrowLeftIcon";
import InfoCircleIcon from "@tSurya/Icons/InfoCircleIcon";

const checkoutPaymentInformationTokens = (WIDGETS as any).checkoutPaymentInformation;
const checkoutNavigationTokens = (WIDGETS as any).checkoutNavigation;

const mapDispatchToProps = {
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    setFulfillmentMethod,
    preloadCheckoutShippingData,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart: makeHandlerChainAwaitable(updatePickUpWarehouseForCart),
};

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    // Use the cart's bill-to rather than the global current bill-to. The payment widget uses
    // the cart-specific record to resolve MGBWTermCode, and the summary must use the same
    // record so a selected payAllOnTerms choice is not normalized back to a card choice.
    const billTo = getBillToState(state, cartState.value?.billToId).value;
    const shipTo = getCurrentShipToState(state).value;
    const customerSettings = getSettingsCollection(state).customerSettings;

    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    return {
        cartId,
        isLoading: cartState.isLoading,
        cart: cartState.value,
        billToProperties: billTo?.properties,
        fulfillmentMethod: cartState.value?.fulfillmentMethod || state.context.session.fulfillmentMethod,
        isBackButtonDisabled: state.pages.checkoutReviewAndSubmit.isPlacingOrder,
        checkoutShippingPageLink: getPageLinkByPageType(state, "CheckoutShippingPage"),
        orderPromotions,
        shippingPromotions,
        allPromotions: promotionsDataView.value,
        discountTotal,
        showPlaceOrderButton: canPlaceOrder(cartState.value),
        placeOrderErrorMessage: state.pages.checkoutReviewAndSubmit.placeOrderErrorMessage,
        showSubmitForApprovalOrder: canSubmitForApprovalOrder(cartState.value),
        hasProductsWithInvalidPrice: hasProductsWithInvalidPrice(cartState.value),
        isUkWebsite: getIsUkWebsite(state),
        canUpdateShipToAddress: billTo?.isGuest || shipTo?.oneTimeAddress || customerSettings.allowBillToAddressEdit || customerSettings.allowShipToAddressEdit,
        updateCartInsteadOfContext: cartState.value?.isAwaitingApproval || !!cartId,
    };
};

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitCartTotalStyles {
    container?: GridContainerProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    buttonsGridItem?: GridItemProps;
    errorMessageGridItem?: GridItemProps;
    placeOrderButton?: ButtonPresentationProps;
    submitForApprovalButton?: ButtonPresentationProps;
    continueButton?: ButtonPresentationProps;
    wizardBackButton?: ButtonPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    defaultFulfillmentMethodRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    defaultFulfillmentMethodRadio?: FieldSetPresentationProps<RadioComponentProps>;
    messages?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
    mobileFixedActions?: InjectableCss;
    backButton?: ButtonPresentationProps;
    continueButtonContent?: InjectableCss;
    wizardBackButtonContent?: InjectableCss;
    gridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    promotionCodeGridItem?: GridItemProps;
    cartTotalWrapper?: InjectableCss;
    errorMessageWrapper?: InjectableCss;
    paymentAllocation?: InjectableCss;
    paymentAllocationRow?: InjectableCss;
    paymentAllocationDueToday?: TypographyPresentationProps;
    paymentAllocationNote?: TypographyPresentationProps;
    paymentAllocationOnTerms?: TypographyPresentationProps;
    paymentAllocationDueTodayAmount?: TypographyPresentationProps;
    paymentAllocationOnTermsAmount?: TypographyPresentationProps;
    separateMgbwChargeNotice?: TypographyPresentationProps;
}

export const cartTotalStyles: CheckoutReviewAndSubmitCartTotalStyles = {
    container: {
        gap: 10,
        css: css`
            [class^="FormInputWrapper"] {
                [class^="InputStyle"][data-test-selector="promotionCodeTextField"] {
                    height: 44px !important;
                    min-height: 44px !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-bottom: 30px;
                        `,
                        css`
                            padding-bottom: 30px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    cartTotalGridItem: { width: 12 },
    buttonsGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
            flex-direction: column;
        `,
    },
    errorMessageGridItem: { width: 12 },
    submitForApprovalButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    defaultFulfillmentMethodRadioGroup: {
        css: css`
            display: block;
            ${RadioStyle} {
                display: inline-block;
                margin-right: 1em;
                & + ${RadioStyle} {
                    margin-top: 0;
                }
            }
        `,
    },
    messages: {
        css: css`
            a > span {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
            }
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: stretch;
            justify-content: center;
            width: 100%;
            height: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        background: ${COLORS.background.main};
                        z-index: ${get(theme, "zIndex.stickyFooter")};
                        padding-bottom: 25px;
                    `,
                    css`
                        margin-left: 16px;
                        padding-bottom: 25px;
                    `,
                    css`
                        padding-bottom: 0;
                    `,
                ])}
        `,
    },
    mobileFixedActions: {
        css: css`
            position: fixed;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: ${({ theme }: { theme: BaseTheme }) => get(theme, "zIndex.stickyFooter")};
            display: none;
            padding: 16px;
            background-color: ${COLORS.background.main};
            border-top: 1px solid ${COLORS.border.main};
            transform: translateY(100%);
            transition: transform 0.3s ease-in-out;

            &[data-visible="true"] {
                transform: translateY(0);
            }

            > [class^="ButtonWrapper"] {
                width: 100%;
            }

            > [class^="ButtonWrapper"]:first-child {
                margin-top: 0 !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: block;
                        `,
                    ],
                    "max",
                )}
        `,
    },

    backButton: {
        variant: "secondary",
        hoverMode: "none",
        css: css`
            background: ${COLORS.primary.contrast};
            color: ${COLORS.text.accent};
            border: 2px solid ${COLORS.text.accent};
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    promotionCodeGridItem: {
        width: 12,
        css: css`
            padding: 0 20px 20px 20px;
        `,
    },
    cartTotalWrapper: {
        css: css`
            background-color: ${COLORS.checkout.lightBg};
            width: 100%;
        `,
    },
    paymentAllocation: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${checkoutPaymentInformationTokens?.summaryAllocation?.rowGap};
            margin: ${checkoutPaymentInformationTokens?.summaryAllocation?.margin};
            padding: ${checkoutPaymentInformationTokens?.summaryAllocation?.padding};
            border-top: ${checkoutPaymentInformationTokens?.summaryAllocation?.borderTop};
        `,
    },
    paymentAllocationRow: {
        css: css`
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: ${checkoutPaymentInformationTokens?.summaryAllocation?.labelAmountGap};
        `,
    },
    paymentAllocationDueToday: {
        css: css`
            color: ${checkoutPaymentInformationTokens?.summaryAllocation?.dueTodayColor};
            font-size: ${checkoutPaymentInformationTokens?.summaryAllocation?.fontSize};
            font-weight: ${checkoutPaymentInformationTokens?.summaryAllocation?.fontWeight};
            line-height: ${checkoutPaymentInformationTokens?.summaryAllocation?.lineHeight};
        `,
    },
    paymentAllocationNote: {
        css: css`
            color: ${COLORS.text.main};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: 15px;
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
        `,
    },
    paymentAllocationOnTerms: {
        css: css`
            color: ${checkoutPaymentInformationTokens?.summaryAllocation?.onTermsColor};
            font-size: ${checkoutPaymentInformationTokens?.summaryAllocation?.fontSize};
            font-weight: ${checkoutPaymentInformationTokens?.summaryAllocation?.fontWeight};
            line-height: ${checkoutPaymentInformationTokens?.summaryAllocation?.lineHeight};
        `,
    },
    paymentAllocationDueTodayAmount: {
        css: css`
            color: ${checkoutPaymentInformationTokens?.summaryAllocation?.dueTodayColor};
            font-size: ${checkoutPaymentInformationTokens?.summaryAllocation?.fontSize};
            font-weight: ${checkoutPaymentInformationTokens?.summaryAllocation?.fontWeight};
            line-height: ${checkoutPaymentInformationTokens?.summaryAllocation?.lineHeight};
            text-align: right;
            white-space: nowrap;
        `,
    },
    paymentAllocationOnTermsAmount: {
        css: css`
            color: ${checkoutPaymentInformationTokens?.summaryAllocation?.onTermsColor};
            font-size: ${checkoutPaymentInformationTokens?.summaryAllocation?.fontSize};
            font-weight: ${checkoutPaymentInformationTokens?.summaryAllocation?.fontWeight};
            line-height: ${checkoutPaymentInformationTokens?.summaryAllocation?.lineHeight};
            text-align: right;
            white-space: nowrap;
        `,
    },
    separateMgbwChargeNotice: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${COLORS.checkout.text.secondary};

            svg {
                vertical-align: text-bottom;
                margin-right: 0.375em;
            }
        `,
    },
    continueButton: {
        variant: "primary",
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.base};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            color: ${COLORS.primary.contrast};
            background-color: ${COLORS.primary.base};
            border-radius: 0;
            width: 100%;
            margin-top: 20px;
            min-height: auto;
            padding: 10px;
            border: 1px solid transparent;

            > span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.base};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                color: ${COLORS.primary.contrast};
            }

            &:hover:not(:disabled) {
                background-color: ${COLORS.primary.base} !important;
                border-color: transparent !important;
                color: ${COLORS.primary.contrast} !important;
            }

            &:hover:not(:disabled) > span {
                color: ${COLORS.primary.contrast} !important;
            }
        `,
    },
    wizardBackButton: {
        variant: "secondary",
        css: css`
            font-family: ${TYPOGRAPHY.button.fontFamily};
            font-weight: ${TYPOGRAPHY.button.weight};
            font-size: ${TYPOGRAPHY.button.size};
            line-height: 1.5;
            color: ${COLORS.primary.base};
            border: 1px solid ${COLORS.primary.base} !important;
            background-color: transparent !important;
            border-radius: 0;
            width: 100%;
            margin-top: 12px;
            min-height: calc(${TYPOGRAPHY.lineHeight.xbase} + 22px);
            height: auto;
            padding: 0;
            -webkit-appearance: none;
            appearance: none;
            box-shadow: inset 0 -1px 0 ${COLORS.primary.base};
            display: flex;
            align-items: center;
            justify-content: center;

            &:hover:not(:disabled) {
                color: ${COLORS.primary.base} !important;
                border-color: ${COLORS.primary.base} !important;
                background-color: transparent !important;
            }

            &:hover:not(:disabled) > span {
                color: ${COLORS.primary.base} !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            display: none;
                        `,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    continueButtonContent: {
        css: css`
            display: inline-flex;
            align-items: center;
            gap: ${checkoutNavigationTokens?.iconGap};

            svg {
                flex: 0 0 auto;
                width: ${checkoutNavigationTokens?.iconWidth};
                height: ${checkoutNavigationTokens?.iconHeight};
            }
        `,
    },
    wizardBackButtonContent: {
        css: css`
            display: inline-flex;
            align-items: center;
            gap: ${checkoutNavigationTokens?.iconGap};

            svg {
                flex: 0 0 auto;
                width: ${checkoutNavigationTokens?.iconWidth};
                height: ${checkoutNavigationTokens?.iconHeight};
            }
        `,
    },
    errorMessageWrapper: {
        css: css`
            padding: 16px 15px;
            border: 2px solid #866a5b;
            &:empty {
                display: none;
            }
        `,
    },
};

const styles = cartTotalStyles;

const CheckoutReviewAndSubmitCartTotal: FC<Props> = ({
    id,
    showPlaceOrderButton,
    cart,
    cartId,
    billToProperties,
    history,
    preloadCheckoutShippingData,
    checkoutShippingPageLink,
    isLoading,
    isBackButtonDisabled,
    shippingPromotions,
    allPromotions,
    discountTotal,
    orderPromotions,
    placeOrderErrorMessage,
    updatePickUpWarehouse,
    setFulfillmentMethod,
    showSubmitForApprovalOrder,
    canUpdateShipToAddress,
    hasProductsWithInvalidPrice,
    isUkWebsite,
    fulfillmentMethod,
    updateCartInsteadOfContext,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart,
}) => {
    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const openWarehouseSelectionModal = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);

    const toasterContext = React.useContext(ToasterContext);
    const handleWarehouseSelected = async (pickUpWarehouse: WarehouseModel) => {
        if (updateCartInsteadOfContext && cart) {
            await updatePickUpWarehouseForCart({ cart, pickUpWarehouse });
        } else {
            await updatePickUpWarehouse({ pickUpWarehouse });
        }

        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
        setIsFindLocationOpen(false);
    };

    const [isFulfillmentMethodOpen, setIsFulfillmentMethodOpen] = React.useState(false);
    const openDeliveryMethodPopup = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFulfillmentMethodOpen(true);
    };

    const modalCloseHandler = () => {
        setIsFulfillmentMethodOpen(false);
    };

    const fulfillmentMethodChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (updateCartInsteadOfContext && cart) {
            setFulfillmentMethodForCart({
                cart,
                fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp",
            });
        } else {
            setFulfillmentMethod({ fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp" });
        }
    };

    const backClickHandler = () => {
        preloadCheckoutShippingData({
            cartId,
            onSuccess: () => {
                const backUrl = cartId ? `${checkoutShippingPageLink!.url}?cartId=${cartId}` : checkoutShippingPageLink!.url;
                history.push(backUrl);
            },
        });
    };
    const parserOptions: HTMLReactParserOptions = {
        replace: node => {
            if (!("name" in node && "children" in node)) {
                return;
            }

            const { name, children, attribs = { style: "" } } = node;
            const { style, class: className, ...otherAttribs } = attribs;

            otherAttribs.className = className;

            if (name === "a") {
                let onClickHandler: ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined;
                for (const key in otherAttribs) {
                    if (key === "ng-click") {
                        if (otherAttribs[key] === "vm.openWarehouseSelectionModal()") {
                            onClickHandler = openWarehouseSelectionModal;
                        }
                        if (otherAttribs[key] === "vm.openDeliveryMethodPopup()") {
                            onClickHandler = openDeliveryMethodPopup;
                        }
                    }
                }
                return (
                    <Link
                        css={style as any}
                        id={otherAttribs.name}
                        {...otherAttribs}
                        onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => onClickHandler && onClickHandler(event)}
                    >
                        {children && domToReact(children as DOMNode[], parserOptions)}
                    </Link>
                );
            }
        },
    };

    const messageKeys = [
        "Checkout_ShipToAddress_Outside_USA_NotAllowed",
        "Checkout_ShippingInstructions_NotAllowed",
        "Freight_Out_Of_Stock_Error",
        "Freight_API_Error",
        "FreightCost_API_Error",
        "Freight_BackOrdered_Items",
    ];

    // Find all message keys that appear in any cart message (including as substrings)
    const matchedMessageKeys = cart?.messages?.length
        ? messageKeys.filter(
              key => !(isUkWebsite && key === "Checkout_ShippingInstructions_NotAllowed") && cart?.messages?.some((msg: string) => msg.includes(key)),
          )
        : [];

    const wizard = useContext(SurCheckoutWizardContext);
    const { paymentSplitChoice } = useSurCheckoutPayment();
    const paymentMethods = cart?.paymentOptions?.paymentMethods;
    const shippingPromotionTotal = getShippingPromotionTotal(shippingPromotions);
    const breakdownModel = getPaymentBreakdown(
        cart,
        !!paymentMethods?.some(isTermsPaymentMethod),
        hasMgbwTermsProperty(billToProperties),
        shippingPromotionTotal,
    );
    const effectiveSplitChoice = breakdownModel.availableChoices.includes(paymentSplitChoice) ? paymentSplitChoice : breakdownModel.defaultChoice;
    const splitTotals = getSplitTotals(breakdownModel, effectiveSplitChoice);
    const termsMethod = paymentMethods?.find(isTermsPaymentMethod);
    const termsLabel = termsMethod?.description || termsMethod?.name;
    const showPaymentSummaryDetails = wizard.currentStep >= CHECKOUT_WIZARD_STEP.Payment;
    const showPaymentAllocation = showPaymentSummaryDetails && breakdownModel.scenario !== "allCreditCard";
    const showSeparateMgbwChargeNotice = showPaymentSummaryDetails && shouldShowSeparateMgbwChargeNotice(breakdownModel, effectiveSplitChoice);

    const renderWizardActions = (isFixedFooter = false) => (
        <>
            {wizard.isLastStep ? (
                <>
                    {showPlaceOrderButton && <PlaceOrderButton styles={styles.continueButton} />}
                    {showSubmitForApprovalOrder && (
                        <SubmitForApprovalButton extendedStyles={isFixedFooter ? styles.continueButton : styles.submitForApprovalButton} />
                    )}
                </>
            ) : (
                <Button
                    {...styles.continueButton}
                    disabled={!wizard.getStepValidity(wizard.currentStep)}
                    onClick={wizard.goToNextStep}
                    data-test-selector={isFixedFooter ? "checkoutReviewAndSubmit_wizardNext_fixed" : "checkoutReviewAndSubmit_wizardNext"}
                >
                    <StyledWrapper as="span" {...styles.continueButtonContent}>
                        {translate(`Continue to ${DEFAULT_CHECKOUT_STEPS[wizard.currentStep]}`)}
                        <CheckoutArrowLeftIcon direction="right" aria-hidden="true" />
                    </StyledWrapper>
                </Button>
            )}
            {wizard.currentStep > wizard.firstStep && (
                <Button
                    {...styles.wizardBackButton}
                    disabled={isBackButtonDisabled}
                    onClick={wizard.goToPreviousStep}
                    data-test-selector={isFixedFooter ? "checkoutReviewAndSubmit_wizardPrevious_fixed" : "checkoutReviewAndSubmit_wizardPrevious"}
                >
                    <StyledWrapper as="span" {...styles.wizardBackButtonContent}>
                        <CheckoutArrowLeftIcon aria-hidden="true" />
                        {translate(`Back to ${DEFAULT_CHECKOUT_STEPS[wizard.currentStep - 2]}`)}
                    </StyledWrapper>
                </Button>
            )}
            {wizard.currentStep === wizard.firstStep && (
                <Button
                    {...styles.wizardBackButton}
                    disabled={isBackButtonDisabled}
                    onClick={wizard.goToPreviousStep}
                    data-test-selector={isFixedFooter ? "checkoutReviewAndSubmit_wizardBackToShipping_fixed" : "checkoutReviewAndSubmit_wizardBackToShipping"}
                >
                    <StyledWrapper as="span" {...styles.wizardBackButtonContent}>
                        <CheckoutArrowLeftIcon aria-hidden="true" />
                        {translate("Back to Shipping Address")}
                    </StyledWrapper>
                </Button>
            )}
        </>
    );

    return (
        <>
            <GridContainer {...styles.container}>
                <StyledWrapper {...styles.cartTotalWrapper}>
                    <CartTotalDisplay
                        cart={cart}
                        isLoading={isLoading}
                        shippingPromotions={shippingPromotions}
                        allPromotions={allPromotions}
                        discountTotal={discountTotal}
                        orderPromotions={orderPromotions}
                        fulfillmentMethod={fulfillmentMethod}
                    />
                    {!isUkWebsite && showPaymentAllocation && (
                        <StyledWrapper {...styles.paymentAllocation} data-test-selector="checkoutReviewAndSubmit_paymentAllocation">
                            {splitTotals.chargedToday > 0 && (
                                <StyledWrapper {...styles.paymentAllocationRow}>
                                    <Typography {...styles.paymentAllocationDueToday}>{translate("Charged Today")}</Typography>
                                    <Typography {...styles.paymentAllocationDueTodayAmount}>
                                        <LocalizedCurrency amount={splitTotals.chargedToday} currencySymbol={cart?.currencySymbol} />
                                    </Typography>
                                </StyledWrapper>
                            )}
                            {splitTotals.onTerms > 0 && (
                                <StyledWrapper {...styles.paymentAllocationRow}>
                                    <Typography {...styles.paymentAllocationOnTerms}>
                                        {translate("On terms")}
                                        {termsLabel ? ` · ${termsLabel}` : ""}
                                    </Typography>
                                    <Typography {...styles.paymentAllocationOnTermsAmount}>
                                        <LocalizedCurrency amount={splitTotals.onTerms} currencySymbol={cart?.currencySymbol} />
                                    </Typography>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    )}
                    {isUkWebsite && wizard.currentStep === CHECKOUT_WIZARD_STEP.Review && (
                        <StyledWrapper {...styles.paymentAllocation} data-test-selector="checkoutReviewAndSubmit_ukPaymentNotice">
                            <Typography {...styles.paymentAllocationNote}>{termsLabel ? `${termsLabel}` : ""}</Typography>
                        </StyledWrapper>
                    )}
                    {showSeparateMgbwChargeNotice && (
                        <GridItem {...styles.promotionCodeGridItem}>
                            <Typography {...styles.separateMgbwChargeNotice} data-test-selector="checkoutReviewAndSubmit_separateMgbwChargeNotice">
                                <InfoCircleIcon />
                                {translate("Items from Mitchell Gold + Bob Williams will be processed as a separate charge on your statement.")}
                            </Typography>
                        </GridItem>
                    )}
                    <GridItem {...styles.promotionCodeGridItem}>
                        <Zone zoneName="PromotionCode" contentId={id} />
                    </GridItem>
                </StyledWrapper>
                <StyledWrapper {...styles.buttonsWrapper}>
                    {cart && <GridItem {...styles.gridItem}>{renderWizardActions()}</GridItem>}
                </StyledWrapper>
                {cart && cart.hasInsufficientInventory === true && (
                    <GridItem {...styles.errorMessageGridItem}>
                        <StyledWrapper {...styles.errorMessageWrapper}>
                            <Typography color="danger" data-test-selector="checkoutReviewAndSubmit_availabilityErrorMessage">
                                {siteMessageWithCustomParserOptions("ReviewAndPay_NotEnoughInventoryForPickup", parserOptions)}
                            </Typography>
                        </StyledWrapper>
                        <FindLocationModal
                            modalIsOpen={isFindLocationOpen}
                            onWarehouseSelected={handleWarehouseSelected}
                            onModalClose={handleFindLocationModalClose}
                            forCart
                            extendedStyles={styles.findLocationModal}
                        />
                        <Modal size={500} headline={translate("Fulfillment Method")} isOpen={isFulfillmentMethodOpen} handleClose={modalCloseHandler}>
                            <RadioGroup
                                value={cart.fulfillmentMethod}
                                onChangeHandler={fulfillmentMethodChangeHandler}
                                {...styles.defaultFulfillmentMethodRadioGroup}
                            >
                                <Radio value={FulfillmentMethod.Ship} {...styles.defaultFulfillmentMethodRadio}>
                                    {translate("Ship")}
                                </Radio>
                                <Radio value={FulfillmentMethod.PickUp} {...styles.defaultFulfillmentMethodRadio}>
                                    {translate("Pick Up")}
                                </Radio>
                            </RadioGroup>
                        </Modal>
                    </GridItem>
                )}
                {placeOrderErrorMessage && (
                    <GridItem {...styles.errorMessageGridItem}>
                        <StyledWrapper {...styles.errorMessageWrapper}>
                            <Typography color="danger" data-test-selector="checkoutReviewAndSubmit_placeOrderErrorMessage">
                                {placeOrderErrorMessage}
                            </Typography>
                        </StyledWrapper>
                    </GridItem>
                )}
                {cart?.taxFailureReason === "InvalidAddress" && canUpdateShipToAddress && (
                    <GridItem {...styles.errorMessageGridItem}>
                        <StyledWrapper {...styles.errorMessageWrapper}>
                            <Typography color="danger">{siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax_UpdateAddress")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                )}
                {cart?.taxFailureReason === "InvalidAddress" && !canUpdateShipToAddress && (
                    <GridItem {...styles.errorMessageGridItem}>
                        <StyledWrapper {...styles.errorMessageWrapper}>
                            <Typography color="danger">{siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                )}
                {hasProductsWithInvalidPrice && (
                    <GridItem {...styles.errorMessageGridItem}>
                        <StyledWrapper {...styles.errorMessageWrapper}>
                            <Typography color="danger">{siteMessage("ReviewAndPay_HasProductsWithInvalidPrice")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                )}
                {cart?.paymentMethod?.name === "AR42" && (
                    <GridItem {...styles.errorMessageGridItem}>
                        <StyledWrapper {...styles.errorMessageWrapper}>
                            <Typography color="danger">{siteMessage("ReviewAndPay_PaymentMethod")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                )}
                {matchedMessageKeys.length > 0 && (
                    <GridItem {...styles.errorMessageGridItem}>
                        <StyledWrapper {...styles.errorMessageWrapper}>
                            {matchedMessageKeys.map((messageKey: string, index: number) => (
                                <Typography key={index} {...styles.messages}>
                                    {siteMessage(`${messageKey}`)}
                                </Typography>
                            ))}
                        </StyledWrapper>
                    </GridItem>
                )}
            </GridContainer>
            <StyledWrapper
                {...styles.mobileFixedActions}
                data-visible="true"
                data-sticky-checkout-actions="true"
                data-sticky-checkout-actions-double="true"
                data-test-selector="checkoutReviewAndSubmit_mobileFixedActions"
            >
                {cart && renderWizardActions(true)}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutReviewAndSubmitCartTotal)),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Cart Total",
    },
};

export default widgetModule;
